import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { OrganisationSettingsService } from '@/api';
import type { CustomEnum } from '@/types/organisation';
import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import {
  models_EntityStatus,
  models_GenderType,
  models_QualificationLevel,
  models_ProfessionalRole,
} from '@/types/employee';

interface EnumOption {
  value: string;
  label: string;
  isSystem: boolean;
  isActive: boolean;
  sortOrder?: number;
}

// Define message descriptors for all system enum labels
const enumMessages: Record<string, Record<string, ReturnType<typeof msg>>> = {
  gender: {
    [models_GenderType.GenderMale]: msg`Male`,
    [models_GenderType.GenderFemale]: msg`Female`,
    [models_GenderType.GenderOther]: msg`Other`,
    [models_GenderType.GenderUnknown]: msg`Unknown`,
  },
  qualification_level: {
    [models_QualificationLevel.QualificationQualified]: msg`Qualified`,
    [models_QualificationLevel.QualificationRecognitionPending]: msg`Recognition Pending`,
    [models_QualificationLevel.QualificationNotQualified]: msg`Not Qualified`,
  },
  role: {
    [models_ProfessionalRole.ProfessionalRoleIntern]: msg`Intern`,
    [models_ProfessionalRole.ProfessionalRolePedagogicAssistent]: msg`Pedagogic Assistant`,
    [models_ProfessionalRole.ProfessionalRoleApprentice]: msg`Apprentice`,
    [models_ProfessionalRole.ProfessionalRoleApprenticeShort]: msg`Apprentice (Short)`,
    [models_ProfessionalRole.ProfessionalRoleEducator]: msg`Educator`,
    [models_ProfessionalRole.ProfessionalRoleGroupHead]: msg`Group Head`,
    [models_ProfessionalRole.ProfessionalRoleMainGroupHead]: msg`Main Group Head`,
    [models_ProfessionalRole.ProfessionalRoleDaycareCenterHead]: msg`Daycare Center Head`,
    [models_ProfessionalRole.ProfessionalRoleCleaningStaff]: msg`Cleaning Staff`,
    [models_ProfessionalRole.ProfessionalRoleJanitor]: msg`Janitor`,
    [models_ProfessionalRole.ProfessionalRoleChef]: msg`Chef`,
    [models_ProfessionalRole.ProfessionalRoleGoverness]: msg`Governess`,
    [models_ProfessionalRole.ProfessionalRoleManagingDirector]: msg`Managing Director`,
    [models_ProfessionalRole.ProfessionalRoleConsultant]: msg`Consultant`,
    [models_ProfessionalRole.ProfessionalRoleOther]: msg`Other`,
  },
  status: {
    [models_EntityStatus.StatusActive]: msg`Active`,
    [models_EntityStatus.StatusInactive]: msg`Inactive`,
    [models_EntityStatus.StatusUpcoming]: msg`Upcoming`,
    [models_EntityStatus.StatusDeleted]: msg`Deleted`,
  },
  type: {
    standard: msg`Standard`,
    vip: msg`VIP`,
  },
  meal_time: {
    Znueni: msg`Breakfast`,
    Zmittag: msg`Lunch`,
    Zvieri: msg`Afternoon Snack`,
    Znacht: msg`Dinner`,
  },
  meal_style: {
    Normal: msg`Normal`,
    Vegi: msg`Vegetarian`,
  },
};

export function useEffectiveEnums(entityType: string, enumField: string) {
  const { orgId } = useAuth();
  const { i18n } = useLingui();

  return useQuery({
    queryKey: ['effective-enums', orgId, entityType, enumField, i18n.locale],
    queryFn: async () => {
      if (!orgId) throw new Error('No organisation ID available');
      
      // Get effective settings (includes both system and custom enums)
      const response = await OrganisationSettingsService.getApiV1SettingsEffective();
      
      const enums: EnumOption[] = [];
      
      // Get custom enums from the API response
      const apiEnums = response?.enums?.[entityType]?.[enumField] || [];
      
      
      // Helper function to get the correct enum key from the API value
      const getEnumKey = (field: string, value: string): string => {
        switch (field) {
          case 'gender':
            // Map API values to enum keys
            switch (value) {
              case 'MALE': return models_GenderType.GenderMale;
              case 'FEMALE': return models_GenderType.GenderFemale;
              case 'OTHER': return models_GenderType.GenderOther;
              case 'UNKNOWN': return models_GenderType.GenderUnknown;
              default: return value;
            }
          case 'qualification_level':
            switch (value) {
              case 'QUALIFIED': return models_QualificationLevel.QualificationQualified;
              case 'RECOGNITION_PENDING': return models_QualificationLevel.QualificationRecognitionPending;
              case 'NOT_QUALIFIED': return models_QualificationLevel.QualificationNotQualified;
              default: return value;
            }
          case 'role':
            switch (value) {
              case 'INTERN': return models_ProfessionalRole.ProfessionalRoleIntern;
              case 'PEDAGOGIC_ASSISTENT': return models_ProfessionalRole.ProfessionalRolePedagogicAssistent;
              case 'APPRENTICE': return models_ProfessionalRole.ProfessionalRoleApprentice;
              case 'APPRENTICE_SHORT': return models_ProfessionalRole.ProfessionalRoleApprenticeShort;
              case 'EDUCATOR': return models_ProfessionalRole.ProfessionalRoleEducator;
              case 'GROUP_HEAD': return models_ProfessionalRole.ProfessionalRoleGroupHead;
              case 'MAIN_GROUP_HEAD': return models_ProfessionalRole.ProfessionalRoleMainGroupHead;
              case 'DAYCARE_CENTER_HEAD': return models_ProfessionalRole.ProfessionalRoleDaycareCenterHead;
              case 'CLEANING_STAFF': return models_ProfessionalRole.ProfessionalRoleCleaningStaff;
              case 'JANITOR': return models_ProfessionalRole.ProfessionalRoleJanitor;
              case 'CHEF': return models_ProfessionalRole.ProfessionalRoleChef;
              case 'GOVERNESS': return models_ProfessionalRole.ProfessionalRoleGoverness;
              case 'MANAGING_DIRECTOR': return models_ProfessionalRole.ProfessionalRoleManagingDirector;
              case 'CONSULTANT': return models_ProfessionalRole.ProfessionalRoleConsultant;
              case 'OTHER': return models_ProfessionalRole.ProfessionalRoleOther;
              default: return value;
            }
          case 'status':
            switch (value) {
              case 'ACTIVE': return models_EntityStatus.StatusActive;
              case 'INACTIVE': return models_EntityStatus.StatusInactive;
              case 'UPCOMING': return models_EntityStatus.StatusUpcoming;
              case 'DELETED': return models_EntityStatus.StatusDeleted;
              default: return value;
            }
          case 'type':
            // For group type, map to lowercase for message lookup
            return value.toLowerCase();
          default:
            return value;
        }
      };

      // First, process all enums from the API (both system and custom)
      apiEnums.forEach((apiEnum: CustomEnum) => {
        if (apiEnum.is_system) {
          // For system enums, try our translation system first, then fall back to API labels
          const messageKey = enumField as keyof typeof enumMessages;
          const enumValue = apiEnum.enum_value || '';
          const enumKey = getEnumKey(enumField, enumValue);
          const message = enumMessages[messageKey]?.[enumKey];

          let label: string;
          if (message) {
            label = i18n._(message);
          } else {
            // Fall back to label_translations or label_default for system enums
            // without hardcoded translations (e.g. menu meal_style, meal_time)
            const currentLocale = i18n.locale || 'en';
            label = apiEnum.label_translations?.[currentLocale] || apiEnum.label_default || enumValue;
          }

          enums.push({
            value: enumValue,
            label,
            isSystem: true,
            isActive: apiEnum.is_active !== false,
            sortOrder: apiEnum.sort_order,
          });
        } else if (apiEnum.is_active !== false) {
          // For custom enums, use the provided labels
          let label = apiEnum.label_default || '';
          
          // Use translation if available for current locale
          const currentLocale = i18n.locale || 'en';
          if (apiEnum.label_translations && apiEnum.label_translations[currentLocale]) {
            label = apiEnum.label_translations[currentLocale];
          }
          
          enums.push({
            value: apiEnum.enum_value || '',
            label: label,
            isSystem: false,
            isActive: true,
            sortOrder: apiEnum.sort_order,
          });
        }
      });
      
      // Sort by sort_order
      enums.sort((a, b) => {
        const orderA = a.sortOrder ?? (a.isSystem ? 0 : 999);
        const orderB = b.sortOrder ?? (b.isSystem ? 0 : 999);
        return orderA - orderB;
      });
      
      // Filter out inactive enums
      const activeEnums = enums.filter(e => e.isActive);
      
      return activeEnums;
    },
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

// Hook to get enum options for a specific field
export function useEnumOptions(entityType: string, enumField: string) {
  const { data: enums = [], isLoading } = useEffectiveEnums(entityType, enumField);
  
  return {
    options: enums.map(e => ({ value: e.value, label: e.label })),
    isLoading,
  };
}